<template>
    <base-dialog :loading="loading" :value="dialog" :title="$t('admin.change_student_coach_title')" :width="440" @input="onInput"
        @close="hide">
        <p class="dialog-text">
            {{ $t('admin.change_student_coach_text') }} <span class="dialog-target">{{ studentName }}</span>
        </p>

        <div class="form-group">
            <span class="form-label">{{ $tc('coach.coach', 2) }}</span>
            <kimsa-combobox :placeholder="$t('common.select_placeholder')" dense solo with-selected-dot
                :items="coachesFiltered" @change="coach = $event" :value="coach" item-value="id" />
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.change')" :disabled="disabled" :loading="loading"
                @cancel="hide" @confirm="changeStudent" />
        </template>
    </base-dialog>
</template>

<script>
import { db, FieldValue } from "@/utils/Firebase";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";

export default {
    name: "StudentChangeCoachDialog",
    components: { KimsaCombobox, BaseDialog, DialogActions },
    props: {
        coaches: {
            type: Array,
            default: null,
        },
        currentStudent: {
            type: [Object, Array],
            default: null,
        },
        currentCoach: {
            type: Object,
            default: null,
        },
        studentMultiple: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            coach: null,
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.coach
        },
        studentName(vm = this) {
            if (vm.studentMultiple) return vm.$t('common.the_students_selected')
            return vm.currentStudent?.name || vm.$t('common.not_available_short')
        },
        coachesFiltered(vm = this) {
            if (!vm.coaches) return []
            if (!vm.currentCoach && !vm.studentMultiple) return []
            return vm.coaches.filter(coach => coach.id !== vm.currentCoach?.id).map(coach => {
                return {
                    id: coach.id,
                    name: `${coach?.name || vm.$t('common.not_available_short')} - ${coach.email}`
                }
            })
        }
    },
    methods: {
        show() {
            this.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.loading = false
            vm.coach = null
        },
        async changeStudent() {
            const vm = this
            vm.loading = true

            try {
                if (vm.studentMultiple) {
                    for (const student of vm.currentStudent) {
                        if (vm.coach.id !== student.studentSubscription.coachId) {
                            await vm.onApplyChangeStudent(student)
                        }
                    }
                } else {
                    await vm.onApplyChangeStudent(vm.currentStudent)
                }

                vm.hide()
                vm.$emit('changed')
                vm.toast(vm.$t('admin.student_changed'))
            } catch (e) {
                console.log('changeStudent error', e?.message)
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.loading = false
            }
        },
        async onApplyChangeStudent(student) {
            const vm = this;
            await db.collection('subscriptions').doc(student.studentSubscription.id).delete()

            let subscription = await db.collection('subscriptions').add({
                studentId: student.id,
                coachId: vm.coach.id,
                paused: false,
                createdAt: FieldValue.serverTimestamp()
            })

            let payload = {
                studentSubscription: {
                    coachDescription: vm.coach?.description || '',
                    coachId: vm.coach.id,
                    coachName: vm.coach?.name || vm.$t('common.not_available_short'),
                    id: subscription.id
                }
            }

            if (student?.group) {
                payload.group = null
                let currentGroup = await db.collection('groups').doc(student.group).get()
                if (currentGroup.data()) {
                    let students = currentGroup.data().students.filter(st => st !== student.id)
                    await db.collection('groups').doc(student.group).update({ students: students })
                }
            }
            if (student?.team) {
                payload.team = null
                let currentTeamRef = await db.collection('teams').doc(student.team.id).get()
                if (currentTeamRef.data()) {
                    let currentTeam = { id: currentTeamRef.id, ...currentTeamRef.data() }
                    let currentGroup = currentTeam?.groups?.find(group => student.team.subgroupId === group.id)
                    let students = currentGroup.students.filter(st => st !== student.id)
                    let groups = JSON.parse(JSON.stringify(currentTeam?.groups))
                    let findGroupIndex = currentTeam?.groups?.findIndex(group => group.id === student.team.subgroupId || null)
                    groups[findGroupIndex].students = students
                    await db.collection('teams').doc(student.team.id).update({ groups: groups })
                }
            }

            await db.collection('users').doc(student.id).update(payload)
        }
    },
}
</script>

<style scoped>
.dialog-text {
    font-size: var(--fs-body);
    color: var(--v-txtSecondary-base);
    margin-bottom: 16px;
}

.dialog-target {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}
</style>